import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DateTime } from "luxon";
import { PageDetailHeader } from "../../components/PageDetailHeader";
import { Container } from "../../components/Container";
import { Card } from "../../components/Card";
import { Button, RouterButtonLink } from "../../components/Button";
import { LoadingContainer } from "../../components/LoadingContainer";
import { InfoBox } from "../../components/InfoBox";
import { Input } from "../../components/Input";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { SectionTitle } from "../../components/SectionTitle";
import { LoadMoreButton } from "../../components/LoadMoreButton";
import {
  useDownloadStoreDebtReportPdfMutation,
  useGetStoreDebtEventsQuery,
  useGetStoreDebtQuery,
} from "../../query/storeDebtsQuery";
import type { ChainId, StoreDebtId, StoreId } from "@KassaroAB/shared/types/ids";
import type {
  RestStoreDebt,
  RestStoreDebtEvent,
} from "@KassaroAB/shared/types/storeDebt";
import { formatMoney } from "../../utils/formatMoney";
import { StoreDebtEventType } from "@KassaroAB/shared/types/enums/StoreDebtEventType";
import { formatDate } from "../../utils/dateUtils";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { getStoreDebtOwed, pairTitle } from "./storeDebtDisplay";

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.x05}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const MetaLabel = styled.span`
  color: ${colors.textMuted};
`;

const MetaValue = styled.span`
  color: ${colors.text};
`;

const EventList = styled.div`
  display: flex;
  flex-direction: column;
`;

const EventRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px 0;
  border-bottom: 1px solid ${colors.border};
  font-size: ${fontSizes.regular}px;

  &:last-child {
    border-bottom: none;
  }
`;

const EventBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  min-width: 0;
`;

const EventStores = styled.span`
  color: ${colors.textMuted};
  font-size: ${fontSizes.small}px;
`;

const EventMeta = styled.span`
  color: ${colors.textMuted};
  white-space: nowrap;
`;

const EmptyText = styled.div`
  padding: ${spacing.x2}px 0;
  color: ${colors.textMuted};
  font-size: ${fontSizes.regular}px;
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.x2}px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
`;

const AmountSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  padding-bottom: ${spacing.x2}px;
  border-bottom: 1px solid ${colors.border};
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  justify-content: flex-end;
  padding-top: ${spacing.x2}px;
  padding-bottom: ${spacing.x2}px;
  border-bottom: 1px solid ${colors.border};
`;

const storeNameForId = (storeDebt: RestStoreDebt, storeId: StoreId) =>
  storeId === storeDebt.storeId1 ? storeDebt.storeName1 : storeDebt.storeName2;

const eventLabel = (type: StoreDebtEventType, t: (key: string) => string) => {
  switch (type) {
    case StoreDebtEventType.GIFT_CARD_REDEMPTION:
      return t("storeDebtsView.eventRedemption");
    case StoreDebtEventType.GIFT_CARD_REFUND:
      return t("storeDebtsView.eventRefund");
    case StoreDebtEventType.CREDIT_NOTE_REDEMPTION:
      return t("storeDebtsView.eventCreditNoteRedemption");
    case StoreDebtEventType.CREDIT_NOTE_REFUND:
      return t("storeDebtsView.eventCreditNoteRefund");
    case StoreDebtEventType.SETTLEMENT:
      return t("storeDebtsView.eventSettlement");
  }
};

const eventStoresLabel = (
  event: RestStoreDebtEvent,
  storeDebt: RestStoreDebt,
  t: (key: string, options: { from: string; to: string }) => string,
) => {
  const from = storeNameForId(storeDebt, event.fromStoreId);
  const to = storeNameForId(storeDebt, event.toStoreId);
  switch (event.type) {
    case StoreDebtEventType.GIFT_CARD_REDEMPTION:
      return t("storeDebtsView.eventRedemptionDetail", { from, to });
    case StoreDebtEventType.GIFT_CARD_REFUND:
      return t("storeDebtsView.eventRefundDetail", { from, to });
    case StoreDebtEventType.CREDIT_NOTE_REDEMPTION:
      return t("storeDebtsView.eventCreditNoteRedemptionDetail", { from, to });
    case StoreDebtEventType.CREDIT_NOTE_REFUND:
      return t("storeDebtsView.eventCreditNoteRefundDetail", { from, to });
    case StoreDebtEventType.SETTLEMENT:
      return t("storeDebtsView.eventSettlementDetail", { from, to });
  }
};

export const StoreDebtDetailView = () => {
  const { t } = useTranslation();
  const { chainId, storeDebtId } = useParams<{
    chainId: ChainId;
    storeDebtId: StoreDebtId;
  }>();
  const [reportStartDate, setReportStartDate] = useState<string | null>(null);
  const [reportEndDate, setReportEndDate] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const {
    data: storeDebt,
    isLoading,
    error,
    refetch,
  } = useGetStoreDebtQuery(chainId, storeDebtId ?? null);
  const {
    data: eventsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingEvents,
    isError: isEventsError,
    refetch: refetchEvents,
  } = useGetStoreDebtEventsQuery(chainId, storeDebtId ?? null);
  const { mutateAsync: downloadReport, isPending: isDownloadingPdf } =
    useDownloadStoreDebtReportPdfMutation(chainId);

  const events = eventsData?.pages.flatMap((page) => page) ?? [];
  const owed = storeDebt ? getStoreDebtOwed(storeDebt) : null;
  const title = pairTitle(storeDebt, t("storeDebtsView.detailTitle"));
  const startValue =
    reportStartDate ??
    (storeDebt
      ? DateTime.fromISO(storeDebt.createdAt).toFormat("yyyy-MM-dd")
      : "");
  const endValue = reportEndDate ?? DateTime.now().toFormat("yyyy-MM-dd");

  const handleDownloadReport = () => {
    if (!storeDebt) {
      return;
    }
    const start = DateTime.fromFormat(startValue, "yyyy-MM-dd").startOf("day");
    const end = DateTime.fromFormat(endValue, "yyyy-MM-dd").endOf("day");
    if (!start.isValid || !end.isValid || start > end) {
      setReportError(t("storeDebtsView.invalidReportDates"));
      return;
    }
    setReportError(null);
    downloadReport({
      storeDebtId: storeDebt.id,
      startDate: start.toJSDate().toISOString(),
      endDate: end.toJSDate().toISOString(),
    }).catch(() => {
      setReportError(t("storeDebtsView.downloadReportFailed"));
    });
  };

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("storeDebtsView.title")}
        parentTo={`/chains/${chainId}/storeDebts`}
        currentLabel={storeDebt ? title : t("common.loading")}
      />
      {isLoading && !storeDebt ? (
        <Card>
          <LoadingContainer message={t("storeDebtsView.loading")} />
        </Card>
      ) : error || !storeDebt || !owed ? (
        <Card>
          <InfoBox variant="error">{t("storeDebtsView.notFound")}</InfoBox>
          <FormActions>
            <Button type="button" $color="secondary" onClick={() => refetch()}>
              {t("common.retry")}
            </Button>
          </FormActions>
        </Card>
      ) : (
        <Card>
          <DetailContent>
            <AmountSection>
              <SectionHeader>
                <SectionTitle>{t("storeDebtsView.amount")}</SectionTitle>
                {owed.outstanding > 0 && (
                  <RouterButtonLink
                    to={`/chains/${chainId}/storeDebts/${storeDebt.id}/settle`}
                  >
                    {t("storeDebtsView.markAsPaid")}
                  </RouterButtonLink>
                )}
              </SectionHeader>
              <p>
                {owed.outstanding === 0
                  ? t("storeDebtsView.settled")
                  : t("storeDebtsView.owes", {
                      debtor: owed.debtorName,
                      creditor: owed.creditorName,
                      amount: formatMoney({
                        amount: owed.outstanding,
                        currency: storeDebt.balanceForStore1.currency,
                      }),
                    })}
              </p>
              <MetaGrid>
                <MetaLabel>{t("storeDebtsView.store1")}</MetaLabel>
                <MetaValue>
                  {storeDebt.storeName1} ({storeDebt.orgNumber1})
                </MetaValue>
                <MetaLabel>{t("storeDebtsView.store2")}</MetaLabel>
                <MetaValue>
                  {storeDebt.storeName2} ({storeDebt.orgNumber2})
                </MetaValue>
              </MetaGrid>
            </AmountSection>

            <div>
              <SectionTitle>{t("storeDebtsView.report")}</SectionTitle>
              <DateRow>
                <FormGroup>
                  <FormLabel htmlFor="store-debt-report-from">
                    {t("storeDebtsView.reportFrom")}
                  </FormLabel>
                  <Input
                    id="store-debt-report-from"
                    type="date"
                    value={startValue}
                    onChange={(changeEvent) =>
                      setReportStartDate(changeEvent.target.value)
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="store-debt-report-to">
                    {t("storeDebtsView.reportTo")}
                  </FormLabel>
                  <Input
                    id="store-debt-report-to"
                    type="date"
                    value={endValue}
                    onChange={(changeEvent) =>
                      setReportEndDate(changeEvent.target.value)
                    }
                  />
                </FormGroup>
              </DateRow>
              {reportError && <InfoBox variant="error">{reportError}</InfoBox>}
              <SectionActions>
                <Button
                  type="button"
                  $color="secondary"
                  disabled={isDownloadingPdf}
                  onClick={handleDownloadReport}
                >
                  {t("storeDebtsView.downloadReport")}
                </Button>
              </SectionActions>
            </div>

            <div>
              <SectionTitle>{t("storeDebtsView.events")}</SectionTitle>
              {isLoadingEvents && events.length === 0 ? (
                <LoadingContainer message={t("storeDebtsView.eventsLoading")} />
              ) : isEventsError ? (
                <>
                  <InfoBox variant="error">
                    {t("storeDebtsView.eventsError")}
                  </InfoBox>
                  <FormActions>
                    <Button
                      type="button"
                      $color="secondary"
                      onClick={() => refetchEvents()}
                    >
                      {t("common.retry")}
                    </Button>
                  </FormActions>
                </>
              ) : events.length === 0 ? (
                <EmptyText>{t("storeDebtsView.eventsEmpty")}</EmptyText>
              ) : (
                <>
                  <EventList>
                    {events.map((storeDebtEvent) => (
                      <EventRow key={storeDebtEvent.id}>
                        <EventBody>
                          <span>{eventLabel(storeDebtEvent.type, t)}</span>
                          <EventStores>
                            {eventStoresLabel(storeDebtEvent, storeDebt, t)}
                          </EventStores>
                        </EventBody>
                        <EventMeta>
                          {formatMoney(storeDebtEvent.amount)} ·{" "}
                          {formatDate(storeDebtEvent.createdAt)}
                        </EventMeta>
                      </EventRow>
                    ))}
                  </EventList>
                  <LoadMoreButton
                    onClick={() => fetchNextPage()}
                    isLoading={isFetchingNextPage}
                    hasMore={hasNextPage}
                  />
                </>
              )}
            </div>
          </DetailContent>
        </Card>
      )}
    </Container>
  );
};
